import { MarkerClusterer } from '@googlemaps/markerclusterer';
import MarkerWithLabelPlugin from 'markerwithlabel';
import MapsApiBaseService from './MapsApiBaseService';

const DEFAULT_MARKER_POINT_POSITION = {
  x: 22,
  y: 0,
};

const EMPTY_CALLBACK = () => {};

export default class MarkersService extends MapsApiBaseService {
  #markers = [];
  #MarkerWithLabel = null;
  #MarkerCluster = null;

  async init() {
    this.#MarkerCluster = new MarkerClusterer({
      algorithmOptions: { radius: 300, maxZoom: 28, extent: 1000 },
      onClusterClick: EMPTY_CALLBACK,
    });
    await super.init();
    this.#MarkerWithLabel = MarkerWithLabelPlugin(this.googleMaps);
  }

  renderMarkers(markersOptions, map) {
    markersOptions.forEach((option) => { this.createMarker(option, map); });
    this.#setMarkersToCluster(map);
  }

  removeAllListeners() {
    this.#markers.forEach((marker) => { this.googleMaps.event.clearInstanceListeners(marker); });
    this.#MarkerCluster?.setOptions({ onClusterClick: EMPTY_CALLBACK });
  }

  addMarkersListener(event, callback) {
    this.#markers.forEach(
      (marker, i) => this.#addMarkerListener(event, marker, () => callback({ marker }, i)),
    );
    this.#MarkerCluster.setOptions({ onClusterClick: (_, cluster) => callback(cluster) });
  }

  createMarker(markerOptions, map) {
    const marker = this.#createMarkerWithLabel(markerOptions, map);

    this.#markers.push(marker);

    return marker;
  }

  showAllMarkers(map) {
    this.#markers.forEach((marker) => { marker.setMap(map); });
  }

  hideAllMarkers() {
    this.#markers.forEach((marker) => { marker.setMap(null); });
  }

  removeAllMarkers() {
    if (this.#markers.length) {
      this.hideAllMarkers();
      this.removeAllListeners();
      this.#markers = [];
    }
  }

  #setMarkersToCluster(map) {
    const [marker] = this.#markers;

    if (marker?.groupRenderer) {
      this.#MarkerCluster.setOptions({ renderer: marker.groupRenderer });
    }

    this.#MarkerCluster.clearMarkers();
    this.#MarkerCluster.setMap(map);
    this.#MarkerCluster.addMarkers(this.#markers);
  }

  #addMarkerListener(event, marker, callback) {
    this.googleMaps.event.addListener(marker, event, callback);
  }

  #createMarkerWithLabel(markerOptions, map) {
    return new this.#MarkerWithLabel({
      ...markerOptions,
      labelAnchor: this.#createPoint(),
      map,
    });
  }

  #createPoint({ x, y } = DEFAULT_MARKER_POINT_POSITION) {
    return new this.googleMaps.Point(x, y);
  }
}
